import type { UrlEntity } from '../domain/note'
import { NOTE_LINK_GENERATED_ATTRIBUTE, NOTE_LINK_OPEN_ATTRIBUTE } from '../utils/markdownWysiwyg'
import { hasSpecificLinkImage, linkDetailsLoading, linkPreviewImageUrl } from './linkMetadata'
import { linkPreviewPresentation } from './linkPreviewCategory'
import { linkTargetKey } from './linkDisplay'

/**
 * Presentation-only decoration of the editor's link units (docs/link-notes/PLAN.md, phases 3-4).
 * A link inside a sentence becomes a chip; a link alone on its line becomes a row. Everything is an
 * attribute or CSS custom property on the label anchor: no generated text nodes, so serialization,
 * selection and the Markdown are untouched. (The editor strips styled spans, so nothing here sits on
 * the unit span itself; the CSS reaches the unit with :has.)
 */
export type LinkUnitDisplay = 'chip' | 'row'

const ZERO_WIDTH = /\u200B/g
const visible = (node: Node) => (node.textContent ?? '').replace(ZERO_WIDTH, '').trim()

function hostOf(href: string): string {
  try { return new URL(href).hostname.replace(/^www\./, '') } catch { return '' }
}

/** A link pill's badge: its site's initial ("G" for maps.google.com), or a dot when it has no site. */
export const linkInitial = (href: string) => (hostOf(href)[0] ?? '·').toUpperCase()

const BLOCK_TAGS = new Set(['P', 'DIV', 'LI', 'UL', 'OL', 'BLOCKQUOTE', 'PRE', 'TABLE', 'HR', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'])
const endsLine = (node: Node) => node.nodeName === 'BR' || (node instanceof Element && BLOCK_TAGS.has(node.tagName))

/** The text on the same line as a unit: siblings back to the previous line break (or block) and on to the next. */
function textOnLineBesides(unit: Element): string {
  let text = ''
  for (let node = unit.previousSibling; node && !endsLine(node); node = node.previousSibling) text = `${visible(node)}${text}`
  for (let node = unit.nextSibling; node && !endsLine(node); node = node.nextSibling) text += visible(node)
  return text
}

/**
 * Row when the link is alone on its line; chip otherwise. A line is a paragraph, a list item or a line of the editor
 * itself (the first lines of a note sit directly in the editor, separated by line breaks), so the same words show the
 * same way whether the note was just typed or has been through undo or a reload. A display the owner chose for the link wins.
 */
export function linkUnitDisplay(unit: Element, anchor: HTMLAnchorElement, chosen?: LinkUnitDisplay): LinkUnitDisplay {
  if (chosen) return chosen
  const block = unit.parentElement
  void anchor
  return block && (block.tagName === 'P' || block.tagName === 'LI' || block.tagName === 'DIV') && textOnLineBesides(unit) === '' ? 'row' : 'chip'
}

export function linkUnitMeta(anchor: HTMLAnchorElement, entity: UrlEntity | null): string {
  const label = visible(anchor)
  const host = hostOf(anchor.getAttribute('href') ?? '')
  if (!entity) return host
  const presentation = linkPreviewPresentation(entity)
  return [presentation.provider === label ? null : presentation.provider, entity.author].filter(Boolean).join(' · ') || host
}

/** The line break after a row (past stray spaces) would add an empty line under the block; mark it so CSS can skip it. */
function markBreakAfterRow(unit: Element): void {
  let node = unit.nextSibling
  while (node && node.nodeType === Node.TEXT_NODE && !(node.textContent ?? '').trim()) node = node.nextSibling
  if (node instanceof HTMLBRElement) node.setAttribute('data-note-link-row-break', 'true')
}

/**
 * The ⋯ at the end of a row, beside its Open arrow: it opens the one link menu (components/LinkActionsMenu.tsx). Text-free: the
 * icon comes from CSS and the label from aria-label. A pill has none: its whole body opens the menu.
 */
function syncRowActions(unit: HTMLElement, wanted: boolean): void {
  const existing = unit.querySelector<HTMLElement>(`:scope > span[${NOTE_LINK_GENERATED_ATTRIBUTE}]`)
  if (!wanted) { existing?.remove(); return }
  if (existing) return
  const holder = document.createElement('span')
  holder.setAttribute(NOTE_LINK_GENERATED_ATTRIBUTE, 'true')
  holder.contentEditable = 'false'
  const button = document.createElement('button')
  button.type = 'button'
  button.setAttribute('data-note-link-action', 'more')
  button.setAttribute('aria-label', 'More link actions')
  button.setAttribute('aria-haspopup', 'menu')
  button.title = 'More link actions'
  holder.append(button)
  unit.append(holder)
}

/**
 * A row's picture is a real <img>, loaded the way the card loads it (cross-origin, no referrer). A CSS background cannot be:
 * the Worker's image proxy answers only requests that carry the app's origin, which a background image does not send, so the
 * picture stayed blank in the native app. A picture that fails to load is hidden and not retried on every pass.
 */
function syncRowArt(unit: HTMLElement, src: string | null): void {
  const existing = unit.querySelector<HTMLImageElement>(':scope > img[data-note-link-art-img]')
  if (!src) { existing?.remove(); return }
  if (existing) {
    if (existing.getAttribute('src') !== src) {
      existing.setAttribute('src', src)
      existing.removeAttribute('data-failed')
      existing.hidden = false
    }
    return
  }
  const image = document.createElement('img')
  image.setAttribute(NOTE_LINK_GENERATED_ATTRIBUTE, 'true')
  image.setAttribute('data-note-link-art-img', 'true')
  image.alt = ''
  image.draggable = false
  image.decoding = 'async'
  image.crossOrigin = 'anonymous'
  image.referrerPolicy = 'no-referrer'
  image.contentEditable = 'false'
  image.addEventListener('error', () => { image.setAttribute('data-failed', 'true'); image.hidden = true })
  image.src = src
  unit.append(image)
}

export function decorateLinkUnits(root: ParentNode, urlByTarget: ReadonlyMap<string, UrlEntity>): void {
  for (const stale of root.querySelectorAll('br[data-note-link-row-break]')) stale.removeAttribute('data-note-link-row-break')
  for (const unit of root.querySelectorAll<HTMLElement>('[data-note-link-unit]')) {
    const anchor = unit.querySelector<HTMLAnchorElement>(`a[href]:not([${NOTE_LINK_OPEN_ATTRIBUTE}])`)
    if (!anchor) continue
    // A unit the card above the note stands in for is out of sight.
    if (unit.closest('[data-link-preview-source="true"]')) continue
    const href = anchor.getAttribute('href') ?? ''
    const entity = urlByTarget.get(linkTargetKey(href)) ?? null
    const set = (name: string, value: string) => { if (anchor.getAttribute(name) !== value) anchor.setAttribute(name, value) }
    const display = linkUnitDisplay(unit, anchor, entity?.display === 'chip' || entity?.display === 'row' ? entity.display : undefined)
    set('data-note-link-display', display)
    if (display === 'row') markBreakAfterRow(unit)
    set('data-note-link-initial', linkInitial(href))
    set('data-note-link-meta', linkUnitMeta(anchor, entity))
    const pending = Boolean(entity && linkDetailsLoading(entity))
    if (pending) {
      set('data-note-link-pending', 'true')
      set('data-note-link-title', visible(anchor))
    } else {
      anchor.removeAttribute('data-note-link-pending')
      anchor.removeAttribute('data-note-link-title')
    }
    // A tap on a pill or a row opens the link menu.
    set('aria-haspopup', 'menu')
    syncRowActions(unit, display === 'row')
    if (anchor.contentEditable !== 'false') anchor.contentEditable = 'false'
    const image = entity && display === 'row' && hasSpecificLinkImage(entity, entity.imageUrl) ? linkPreviewImageUrl(entity.imageUrl) : null
    if (image) set('data-note-link-art', 'true')
    else anchor.removeAttribute('data-note-link-art')
    syncRowArt(unit, image)
  }
}
